// src/Components/moderation/UserManagementPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, FileText, AlertTriangle, Ban, RefreshCw, X, ArrowLeft, CheckCircle, Download } from 'lucide-react';
import { moderationAPI } from '../../services/api';
import * as XLSX from 'xlsx';

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function UserManagementPage() {
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const debouncedSearch           = useDebounced(search);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(true);
  const [error, setError]         = useState(null);
  const [warning, setWarning]     = useState(null);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detail, setDetail]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actioning, setActioning]     = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'ban'
  const [exporting, setExporting] = useState(false);

  const exportAllUsers = async () => {
    setExporting(true);
    try {
      let all = [];
      let p = 1;
      let keepGoing = true;
      while (keepGoing) {
        const res = await moderationAPI.searchUsers(debouncedSearch, p);
        const list = res?.data?.users || [];
        all = all.concat(list);
        keepGoing = list.length > 0 && list.length >= 20;
        p += 1;
        if (p > 100) break;
      }
      const rows = all.map(u => ({
        ID: u.id,
        Name: u.first_name || '',
        Email: u.email || '',
        Status: u.status || 'unknown',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      XLSX.writeFile(wb, `beetroot-users-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert(`Export failed: ${err}`);
    } finally {
      setExporting(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    try {
      const res = await moderationAPI.searchUsers(debouncedSearch, 1);
      if (res.success) {
        const list = res.data.users || [];
        setUsers(list);
        setWarning(res.data.warning || null);
        setHasMore(list.length > 0 && (res.data.hasMore ?? list.length >= 20));
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  const loadMoreUsers = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await moderationAPI.searchUsers(debouncedSearch, nextPage);
      if (res.success) {
        const list = res.data.users || [];
        setUsers(prev => [...prev, ...list]);
        setPage(nextPage);
        setHasMore(list.length > 0 && (res.data.hasMore ?? list.length >= 20));
      }
    } catch (err) {
      alert(`Failed to load more users: ${err}`);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openUserDetail = async (userId) => {
    setSelectedUserId(userId);
    setDetailLoading(true);
    try {
      const res = await moderationAPI.getUserDetail(userId);
      if (res.success) setDetail(res.data);
    } catch (err) {
      alert(`Failed to load user detail: ${err}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSuspend = async () => {
    setActioning(true);
    try {
      await moderationAPI.suspendUser(selectedUserId);
      await openUserDetail(selectedUserId);
    } catch (err) {
      alert(`Suspend failed: ${err}`);
    } finally {
      setActioning(false);
      setConfirmAction(null);
    }
  };

  const handleBan = async () => {
    setActioning(true);
    try {
      await moderationAPI.banUser(selectedUserId);
      await openUserDetail(selectedUserId);
    } catch (err) {
      alert(`Ban failed: ${err}`);
    } finally {
      setActioning(false);
      setConfirmAction(null);
    }
  };

  const handleReinstate = async () => {
    setActioning(true);
    try {
      await moderationAPI.reinstateUser(selectedUserId);
      await openUserDetail(selectedUserId);
    } catch (err) {
      alert(`Reinstate failed: ${err}`);
    } finally {
      setActioning(false);
      setConfirmAction(null);
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      active:    'bg-green-100 text-green-700',
      suspended: 'bg-yellow-100 text-yellow-700',
      banned:    'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg[status] || 'bg-gray-100 text-gray-500'}`}>
        {status || 'unknown'}
      </span>
    );
  };

  const filteredUsers = statusFilter === 'all'
    ? users
    : users.filter(u => u.status === statusFilter);

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selectedUserId) {
    return (
      <div className="space-y-6">
        <button onClick={() => { setSelectedUserId(null); setDetail(null); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition">
          <ArrowLeft size={16} /> Back to User List
        </button>

        {detailLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : !detail?.user ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center">
            <p className="text-gray-400">User not found or detail unavailable.</p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                    {(detail.user.first_name?.[0] || detail.user.email?.[0] || '?').toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {detail.user.first_name || detail.user.email || detail.user.id}
                    </h2>
                    <p className="text-white/70 text-sm">{detail.user.email}</p>
                    <div className="mt-1">{statusBadge(detail.user.status)}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmAction('suspend')}
                    className="flex items-center gap-1.5 px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg text-sm transition">
                    <AlertTriangle size={14} /> Suspend
                  </button>
                  <button onClick={() => setConfirmAction('ban')}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-sm transition">
                    <Ban size={14} /> Ban
                  </button>
                  {(detail.user.status === 'suspended' || detail.user.status === 'banned') && (
                    <button onClick={() => setConfirmAction('reinstate')}
                      className="flex items-center gap-1.5 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-sm transition">
                      <CheckCircle size={14} /> Reinstate
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                <p className="text-xs text-gray-400 font-medium">Posts</p>
                <p className="text-2xl font-bold mt-1">{detail.posts?.length || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                <p className="text-xs text-gray-400 font-medium">Reports Against User</p>
                <p className="text-2xl font-bold mt-1 text-red-600">{detail.reportsAgainstUser?.length || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                <p className="text-xs text-gray-400 font-medium">Reports Made By User</p>
                <p className="text-2xl font-bold mt-1">{detail.reportsMadeByUser?.length || 0}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText size={17} className="text-gray-400" /> Post History
              </h3>
              {detail.posts?.length > 0 ? (
                <div className="space-y-2">
                  {detail.posts.slice(0, 10).map(p => (
                    <div key={p.id} className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-sm">
                      <p className="font-medium truncate">{p.title || 'Untitled'}</p>
                      <p className="text-xs text-gray-400">{p.status} · {new Date(p.date_created).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No posts</p>
              )}
            </div>

            {detail.reportsAgainstUser?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle size={17} className="text-red-500" /> Reports Against This User
                </h3>
                <div className="space-y-2">
                  {detail.reportsAgainstUser.map(r => (
                    <div key={r.id} className="p-3 bg-red-50 rounded-lg text-sm">
                      <p className="font-medium">{r.reason}</p>
                      <p className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()} · {r.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {confirmAction && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold mb-2">
                {confirmAction === 'suspend' ? 'Suspend Account?' : confirmAction === 'ban' ? 'Ban Account?' : 'Reinstate Account?'}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {confirmAction === 'suspend'
                  ? 'This temporarily restricts the account. It can be reversed.'
                  : confirmAction === 'ban'
                    ? 'This is a serious, harder-to-reverse action. The user will lose platform access.'
                    : 'This restores full account access.'}
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmAction(null)} disabled={actioning}
                  className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition">
                  Cancel
                </button>
                <button
                  onClick={
                    confirmAction === 'suspend' ? handleSuspend :
                    confirmAction === 'ban' ? handleBan :
                    handleReinstate
                  }
                  disabled={actioning}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                  {actioning ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List/search view ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <User className="text-red-600" size={24} />
            User Management
          </h1>
          <p className="text-sm text-gray-500">Search users, view history, take action</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchUsers}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-red-400 transition">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={exportAllUsers} disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700 transition disabled:opacity-50">
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export Users'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by email or name..."
            className="pl-9 pr-8 py-2.5 w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {[
            { value: 'all', label: 'All Users' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'banned', label: 'Banned' },
          ].map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                statusFilter === f.value ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{warning}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        {loading ? (
          <div className="space-y-3">{Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16">
            <User size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No users found</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredUsers.map(u => (
              <button key={u.id} onClick={() => openUserDetail(u.id)}
                className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left transition">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-500 to-gray-700 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{u.first_name || u.email || u.id}</p>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                </div>
                {statusBadge(u.status)}
              </button>
            ))}
            {hasMore && !loading && filteredUsers.length > 0 && (
              <div className="pt-4 text-center">
                <button onClick={loadMoreUsers} disabled={loadingMore}
                  className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-400 transition disabled:opacity-50">
                  {loadingMore ? 'Loading...' : 'Load More Users'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}